// Vecindus Login Page - Modern SaaS Style (Based on Classic Mi ResidenciApp Design)
// Clean, professional, with device mockup showcase

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Building2, Mail, Lock, Eye, EyeOff, ArrowRight, 
  CheckCircle, Smartphone, Users, Shield, CreditCard
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
// Jaime directive 2026-10-12: demo autosign section extracted to a shared
// component so the public landing renders the same section (reuse, not removal).
import DemoSection from '../components/DemoSection';
import { Logo } from '../components/Logo';
import { getPostLoginDestinationFromStorage } from '../lib/postLoginDestination';
// P0 website overhaul (master plan 2026-10-09): hide-don't-eliminate flag gate
// for the fabricated "+10k usuarios" trust chip (constants/featureFlags.ts).
import { SOCIAL_PROOF_ENABLED } from '../constants/featureFlags';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, verify2FA, completeLoginWithPayload } = useAuth();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  // P1 launch-blocker fix (2026-10-09): when login is blocked with code
  // EMAIL_NOT_VERIFIED, offer a visible resend-verification affordance.
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // Identity batch 2 (item B, 2026-09-16): "Entrar con código" — email OTP
  // as a SECONDARY flow; identifier+password stays primary.
  const [otpMode, setOtpMode] = useState<'none' | 'email' | 'code'>('none');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMsg, setOtpMsg] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  // P0 (master plan decision A): the landing hero "Explora la demo" CTA routes
  // to /login#demo — scroll the visitor straight to the demo autosign chips.
  const location = useLocation();
  useEffect(() => {
    if (location.hash === '#demo') {
      document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [location.hash]);

  const navigateAfterLogin = () => {
    // 2026-09-18: destination logic moved to lib/postLoginDestination.ts so the
    // "/" root auth gate (PWA start_url) redirects to the same place.
    navigate(getPostLoginDestinationFromStorage(), { replace: true });
  };

  const handleOtpRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setOtpMsg(''); setOtpBusy(true);
    try {
      const res = await fetch('/api/auth/request-email-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail.trim() })
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('login.otp.error.wait'));
        return;
      }
      setOtpMode('code');
      setOtpMsg(t('login.otp.sentMessage'));
    } catch {
      setError(t('login.otp.error.sendFailed'));
    } finally { setOtpBusy(false); }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setOtpBusy(true);
    try {
      const res = await fetch('/api/auth/verify-email-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail.trim(), code: otpCode })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || t('login.otp.error.invalidCode'));
        setOtpCode('');
        return;
      }
      const okLogin = await completeLoginWithPayload(data);
      if (!okLogin) { setRequires2FA(true); return; }
      navigateAfterLogin();
    } catch {
      setError(t('login.otp.error.verifyFailed'));
    } finally { setOtpBusy(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const authenticated = await login(email, password);
      if (!authenticated) { setRequires2FA(true); return; }
      // Login persists the canonical user payload; use its concrete backend role
      // for the first destination instead of sending every role to HOME.
      const storedUser = localStorage.getItem('user');
      const role = storedUser ? JSON.parse(storedUser)?.role : undefined;
      const destination = role === 'super_admin'
        ? '/super-admin'
        : role === 'admin' || role === 'sub_admin'
          ? '/admin'
          : role === 'treasurer' || role === 'directive'
            ? '/directive'
            : role === 'security'
              ? '/security-dashboard'
              : '/home/dashboard';
      navigate(destination, { replace: true });
    } catch (err: any) {
      // D1 fix (2026-10-01): 429 (per-IP login rate limit) must not read as
      // "Credenciales inválidas" — show the backend's wait-time message.
      if (err?.status === 429 || err?.isRateLimit) {
        setError(err.message || t('login.error.tooManyAttempts'));
      } else if (err?.code === 'EMAIL_NOT_VERIFIED') {
        // P1 launch-blocker fix (2026-10-09): visible block + resend affordance
        // instead of a generic "credenciales inválidas".
        setShowResendVerification(true);
        setResendMsg('');
        setError(err.message || 'Debes verificar tu correo antes de iniciar sesión.');
      } else {
        setError(requires2FA ? t('login.error.invalid2FASession') : t('login.error.invalidCredentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: CreditCard, text: t('login.featurePaymentsTitle'), desc: t('login.featurePaymentsDesc') },
    { icon: Shield, text: t('login.featureSecurityTitle'), desc: t('login.featureSecurityDesc') },
    { icon: Users, text: t('login.featureManagementTitle'), desc: t('login.featureManagementDesc') },
  ];

  return (
    <div className="min-h-dvh bg-white pb-[env(safe-area-inset-bottom)]">
      {/* Navigation */}
      <nav className="border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="https://vecindus.com" className="cursor-pointer hover:opacity-80 transition-opacity">
              <div className="flex items-center gap-2">
                <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                  <rect width="40" height="40" rx="10" fill="#36CCD8"/>
                  <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
                  <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
                </svg>
                <span className="text-xl font-bold text-gray-900">Vecindus</span>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{t('login.noAccount')}</span>
              <button 
                onClick={() => navigate('/register')}
                className="text-[#36CCD8] font-semibold hover:text-[#2DB8C4] transition-colors"
              >
                {t('login.toRegister')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Side - Visual Showcase */}
          <div className="order-2 lg:order-1">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {t('login.heroTitlePrefix')}{' '}<span className="text-[#36CCD8]">{t('login.heroTitleAccent')}</span>{' '}{t('login.heroTitleSuffix')}
            </h1>
            <p className="text-lg text-gray-600 mb-10">
              {t('login.heroSubtitle')}
            </p>

            {/* Features */}
            <div className="space-y-6 mb-10">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-[#36CCD8]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{feature.text}</h3>
                    <p className="text-sm text-gray-500">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Phone Mockup Visual */}
            <div className="relative mt-8">
              <div className="absolute inset-0 bg-gradient-to-r from-[#36CCD8]/5 to-transparent rounded-3xl" />
              <div className="relative bg-gray-50 rounded-3xl p-6 border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="w-32 h-64 bg-[#2D3748] rounded-3xl border-4 border-gray-300 shadow-xl relative overflow-hidden">
                    {/* Mock Phone Screen */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#36CCD8] to-[#2D3748]">
                      <div className="p-3 pt-8">
                        <div className="w-8 h-8 bg-white/20 rounded-lg mx-auto mb-4" />
                        <div className="space-y-2">
                          <div className="h-2 bg-white/30 rounded w-3/4 mx-auto" />
                          <div className="h-2 bg-white/20 rounded w-1/2 mx-auto" />
                        </div>
                        <div className="mt-6 space-y-2 px-2">
                          <div className="h-8 bg-white/20 rounded-lg" />
                          <div className="h-8 bg-white/20 rounded-lg" />
                          <div className="h-8 bg-white/20 rounded-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 mb-2">{t('login.appMobile')}</p>
                    <p className="text-sm text-gray-500 mb-4">{t('login.appPlatforms')}</p>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">📱 iOS</span>
                      <span className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">🤖 Android</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="order-1 lg:order-2">
            <div className="max-w-md mx-auto lg:mx-0">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('login.welcomeBack')}</h2>
                  <p className="text-gray-500">{t('login.enterCredentials')}</p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                    <span className="text-red-500">⚠️</span>
                    <p className="text-red-700 text-sm font-medium">{error}</p>
                  </div>
                )}

                {showResendVerification && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                    <p className="text-amber-800 text-sm">
                      Tu cuenta aún no verificó su correo. Revisa tu bandeja de entrada o solicita un nuevo enlace.
                    </p>
                    {resendMsg && <p className="text-green-700 text-sm font-medium">{resendMsg}</p>}
                    <button
                      type="button"
                      disabled={resendBusy}
                      onClick={async () => {
                        setResendBusy(true);
                        setResendMsg('');
                        try {
                          const res = await fetch('/api/auth/resend-verification-request', {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email })
                          });
                          const data = await res.json().catch(() => ({}));
                          setResendMsg(data.message || 'Si el correo corresponde a una cuenta sin verificar, se envió un enlace de verificación.');
                        } catch {
                          setResendMsg('No se pudo solicitar el reenvío. Intenta de nuevo.');
                        } finally {
                          setResendBusy(false);
                        }
                      }}
                      className="w-full py-2.5 bg-[#36CCD8] text-white font-semibold rounded-xl disabled:opacity-50 text-sm"
                    >
                      {resendBusy ? 'Enviando…' : 'Reenviar correo de verificación'}
                    </button>
                  </div>
                )}

                {requires2FA ? (
                  <form onSubmit={async (e) => { e.preventDefault(); setError(''); setIsLoading(true); try { await verify2FA(localStorage.getItem('vecindus_2fa_pending') || '', twoFactorCode); navigate('/home/dashboard', { replace: true }); } catch (err: any) {
                      const status = err?.response?.status;
                      const respError: string = err?.response?.data?.error || '';
                      if (status === 429) setError(t('login.error.tooManyAttempts'));
                      else if (status === 401 && /token/i.test(respError)) setError(t('login.error.sessionExpired'));
                      else setError(t('login.error.invalid2FA'));
                      setTwoFactorCode(''); } finally { setIsLoading(false); } }} className="space-y-5">
                    <p className="text-gray-600">{t('login.twoFa.prompt')}</p>
                    <input autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={6} value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-center text-2xl tracking-[0.5em] text-gray-900" placeholder="000000" required />
                    <button type="submit" disabled={isLoading || twoFactorCode.length !== 6} className="w-full py-3.5 bg-[#36CCD8] text-white font-bold rounded-xl disabled:opacity-50">{isLoading ? t('shared.verifying') : t('shared.verify')}</button>
                    <button type="button" onClick={() => { setRequires2FA(false); setTwoFactorCode(''); localStorage.removeItem('vecindus_2fa_pending'); }} className="w-full text-sm text-[#36CCD8]">{t('login.twoFa.useBackup')}</button>
                  </form>
                ) : (<form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('login.emailOrPhone')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        inputMode="text"
                        autoComplete="username"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500"
                        placeholder={t('login.emailOrPhonePlaceholder')}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('auth.password')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none">
                        <Lock className="w-5 h-5" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-14 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-[#36CCD8] hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-[#36CCD8] rounded border-gray-300 focus:ring-[#36CCD8]" />
                      <span className="text-sm text-gray-600">{t('login.rememberMe')}</span>
                    </label>
                    <button 
                      type="button"
                      onClick={() => navigate('/forgot-password')} 
                      className="text-sm font-semibold text-[#36CCD8] hover:text-[#2DB8C4]"
                    >
                      {t('shared.forgotPassword')}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 bg-[#36CCD8] hover:bg-[#2DB8C4] text-white font-bold rounded-xl shadow-lg shadow-[#36CCD8]/25 hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? (
                      t('shared.loading')
                    ) : (
                      <>
                        {t('login.submit')} <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>)}
                {!requires2FA && otpMode === 'none' && (
                  <button
                    type="button"
                    onClick={() => { setOtpMode('email'); setOtpEmail(email.includes('@') ? email : ''); setError(''); }}
                    className="mt-4 w-full text-sm font-semibold text-[#36CCD8] hover:text-[#2DB8C4] transition-colors"
                  >
                    {t('login.otp.enterWithCode')}
                  </button>
                )}
                {!requires2FA && otpMode === 'email' && (
                  <form onSubmit={handleOtpRequest} className="mt-5 space-y-4 border-t border-gray-100 pt-5">
                    <p className="text-sm text-gray-600">{t('login.otp.emailPrompt')}</p>
                    <input
                      type="email"
                      autoComplete="email"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500"
                      placeholder={t('auth.emailPlaceholder')}
                      required
                    />
                    <button type="submit" disabled={otpBusy || !otpEmail.trim()} className="w-full py-3 bg-[#36CCD8] text-white font-bold rounded-xl disabled:opacity-50">
                      {otpBusy ? t('shared.sending') : t('login.otp.sendCode')}
                    </button>
                    <button type="button" onClick={() => { setOtpMode('none'); setError(''); }} className="w-full text-sm text-gray-500 hover:text-gray-700">{t('login.otp.usePassword')}</button>
                  </form>
                )}
                {!requires2FA && otpMode === 'code' && (
                  <form onSubmit={handleOtpVerify} className="mt-5 space-y-4 border-t border-gray-100 pt-5">
                    {otpMsg && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">{otpMsg}</p>}
                    <input
                      autoFocus
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-center text-2xl tracking-[0.5em] text-gray-900"
                      placeholder="000000"
                      required
                    />
                    <button type="submit" disabled={otpBusy || otpCode.length !== 6} className="w-full py-3 bg-[#36CCD8] text-white font-bold rounded-xl disabled:opacity-50">
                      {otpBusy ? t('shared.verifying') : t('shared.verifyCode')}
                    </button>
                    <button type="button" onClick={() => { setOtpMode('email'); setOtpCode(''); setOtpMsg(''); setError(''); }} className="w-full text-sm text-gray-500 hover:text-gray-700">{t('login.otp.changeEmail')}</button>
                    <button type="button" onClick={() => { setOtpMode('none'); setOtpCode(''); setOtpMsg(''); setError(''); }} className="w-full text-sm text-gray-500 hover:text-gray-700">{t('login.otp.usePassword')}</button>
                  </form>
                )}
              </div>

              {/* Demo autosign chips (onboarding/frontend-enable, 2026-10-01):
                  wired to the LIVE POST /api/demo/autosign endpoint — each chip
                  signs into the demo account for that role (normal login-shaped
                  session) and lands on the dashboard with the demo condo.
                  Jaime directive 2026-10-12: now the SHARED DemoSection (also
                  rendered on the landing); /login#demo anchor unchanged.
                  onError keeps demo errors in this page's shared error box. */}
              <DemoSection onError={setError} />

              {/* Trust Indicators. P0 overhaul (master plan 2026-10-09, zero-user
                  reality + Jaime hide-don't-eliminate directive 2026-10-12): the
                  fabricated "+10k usuarios" chip is HIDDEN, not deleted. With
                  SOCIAL_PROOF_ENABLED=false the strip shows only TRUE chips.
                  Re-enable the old strip ONLY with real usage numbers (real VE
                  admin counts from the product). */}
              <div className="mt-8 text-center">
                {SOCIAL_PROOF_ENABLED ? (
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#36CCD8]" />
                      <span>{t('login.trust.secure')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#36CCD8]" />
                      <span>{t('login.trust.verified')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#36CCD8]" />
                      <span>{t('login.trust.users')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#36CCD8]" />
                      <span>{t('login.trust.free')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#36CCD8]" />
                      <span>{t('login.trust.noCommitment')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#36CCD8]" />
                      <span>{t('login.trust.realDemo')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <a href="https://vecindus.com" className="cursor-pointer hover:opacity-80 transition-opacity">
              <div className="flex items-center gap-2">
                <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                  <rect width="40" height="40" rx="10" fill="#36CCD8"/>
                  <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
                  <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
                </svg>
                <span className="font-semibold text-gray-900">Vecindus</span>
              </div>
            </a>
            <p className="text-sm text-gray-500">
              {t('login.footer.copyright')}
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <button onClick={() => navigate('/privacy')} className="hover:text-[#36CCD8]">{t('login.footer.privacy')}</button>
              <button onClick={() => navigate('/terms')} className="hover:text-[#36CCD8]">{t('login.footer.terms')}</button>
              <button onClick={() => navigate('/support')} className="hover:text-[#36CCD8]">{t('login.footer.support')}</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
